import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, LoginRequest, LoginResponse, User, Permission, PermissionKey } from '../../types/auth';
import { authService } from '../../services/authService';

const TOKEN_KEY = 'talabat_token';
const REFRESH_TOKEN_KEY = 'talabat_refresh_token';
const USER_KEY = 'talabat_user';
const TOKEN_EXPIRY_KEY = 'talabat_token_expiry';

// Helper functions for localStorage
const loadAuthFromStorage = (): Partial<AuthState> => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);    if (token && refreshToken && userStr && expiryStr) {
      const user = JSON.parse(userStr);
      const tokenExpiry = new Date(expiryStr);
      const isExpired = tokenExpiry.getTime() <= Date.now();

      if (!isExpired) {
        return {
          isAuthenticated: true,
          token,
          refreshToken,
          user,
          tokenExpiry: expiryStr, // Store as string, not Date object
        };
      }
    }
  } catch (error) {
    console.error('Error loading auth from storage:', error);
  }
  return {
    isAuthenticated: false,
    token: null,
    refreshToken: null,
    user: null,
    tokenExpiry: null,
  };
};

const saveAuthToStorage = (token: string, refreshToken: string, user: User, expiresAt: string) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt);
  } catch (error) {
    console.error('Error saving auth to storage:', error);
  }
};

const clearAuthFromStorage = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  } catch (error) {
    console.error('Error clearing auth from storage:', error);
  }
};

const initialState: AuthState = {
  isLoading: false,
  error: null,
  isAuthenticated: false,
  user: null,
  token: null,
  refreshToken: null,
  tokenExpiry: null,
  ...loadAuthFromStorage(),
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      saveAuthToStorage(response.token, response.refreshToken, response.user, response.expiresAt);
      return response;
    } catch (error: any) {
      // Extract detailed error information from the API response
      const apiResponse = error.response?.data;
      let errorMessage = 'Login failed';
      
      if (apiResponse) {
        // Use the detailed message from the API
        errorMessage = apiResponse.message || errorMessage;
        
        // Add additional context for specific error types
        if (apiResponse.error === 'account_locked' && apiResponse.lockoutEndTime) {
          const lockoutEnd = new Date(apiResponse.lockoutEndTime);
          const now = new Date();
          const minutesLeft = Math.ceil((lockoutEnd.getTime() - now.getTime()) / (1000 * 60));
          if (minutesLeft > 0) {
            errorMessage += ` Account will be unlocked in ${minutesLeft} minute(s).`;
          }
        } else if (apiResponse.error === 'invalid_password' && apiResponse.failedAttempts) {
          const attemptsLeft = 5 - apiResponse.failedAttempts;
          if (attemptsLeft > 0) {
            errorMessage += ` ${attemptsLeft} attempt(s) remaining before account lockout.`;
          }
        }
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const refreshAuthToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const { refreshToken: currentRefreshToken } = state.auth;
      
      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken({ refreshToken: currentRefreshToken });
      
      // Update storage with new tokens
      const userStr = localStorage.getItem(USER_KEY);
      if (userStr) {
        const user = JSON.parse(userStr);
        saveAuthToStorage(response.token, response.refreshToken, user, response.expiresAt);
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Token refresh failed';
      return rejectWithValue(errorMessage);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const { token } = state.auth;
      
      if (token) {
        await authService.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthFromStorage();
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkStatus',
  async (_, { getState, dispatch }) => {
    const state = getState() as { auth: AuthState };
    const { tokenExpiry, isAuthenticated, refreshToken } = state.auth;

    if (!isAuthenticated || !tokenExpiry || !refreshToken) {
      throw new Error('Not authenticated');
    }

    const now = new Date();
    const expiry = new Date(tokenExpiry);
    const timeToExpiry = expiry.getTime() - now.getTime();    // If token expires in less than 5 minutes, refresh it
    if (timeToExpiry < 5 * 60 * 1000) {
      return dispatch(refreshAuthToken()).unwrap();
    }

    return { isValid: true };
  }
);

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { getState, dispatch }) => {
    const state = getState() as { auth: AuthState };
    const { isAuthenticated } = state.auth;

    if (isAuthenticated) {
      try {
        await dispatch(checkAuthStatus()).unwrap();
      } catch (error) {
        // If auth check fails, clear the auth state
        clearAuthFromStorage();
        throw error;
      }
    }

    return { initialized: true };
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      if (state.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(state.user));
      }
    },
    // Synchronous logout for immediate cleanup (e.g., on 401 responses)
    forceLogout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.tokenExpiry = null;
      state.error = null;
      state.isLoading = false;
      clearAuthFromStorage();
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })      .addCase(login.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.tokenExpiry = action.payload.expiresAt; // Store as string, not Date object
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        state.error = action.payload as string;
        clearAuthFromStorage();
      })
        // Refresh Token
      .addCase(refreshAuthToken.pending, (state) => {
        state.isLoading = true;
      })      .addCase(refreshAuthToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.tokenExpiry = action.payload.expiresAt; // Store as string, not Date object
        state.error = null;
      })
      .addCase(refreshAuthToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        state.error = action.payload as string;
        clearAuthFromStorage();
      })
      
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        state.error = null;
      })
        // Check Auth Status
      .addCase(checkAuthStatus.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        clearAuthFromStorage();
      })
      
      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        clearAuthFromStorage();
      });
  },
});

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectIsLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;

// Permission checking selectors
export const selectUserPermissions = (state: { auth: AuthState }): Permission[] => {
  return state.auth.user?.permissions || [];
};

export const selectUserRoles = (state: { auth: AuthState }): string[] => {
  return state.auth.user?.roles?.map(role => role.name) || [];
};

export const selectHasPermission = (state: { auth: AuthState }, permission: PermissionKey): boolean => {
  const userPermissions = selectUserPermissions(state);
  
  // SuperAdmin has all permissions
  if (userPermissions.some(p => p.name === '*:*')) {
    return true;
  }
  
  // Check for exact permission match
  return userPermissions.some(p => p.name === permission);
};

export const selectHasRole = (state: { auth: AuthState }, roleName: string): boolean => {
  const userRoles = selectUserRoles(state);
  return userRoles.includes(roleName);
};

export const selectIsSuperAdmin = (state: { auth: AuthState }): boolean => {
  return selectHasRole(state, 'SuperAdmin');
};

export const selectIsAdmin = (state: { auth: AuthState }): boolean => {
  const roles = selectUserRoles(state);
  return roles.includes('Admin') || roles.includes('SuperAdmin');
};

export const { clearError, updateUser, forceLogout } = authSlice.actions;
export default authSlice.reducer;
